import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	formatInsuranceModifiedAt,
	insuranceHasModificationHistory,
	parseInsuranceModifiedBy,
} from "@/lib/insuranceModificationMeta";
import type { CreateInsuranceFormData } from "@/types/insurance/insurance";
import { History } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
	insurance: CreateInsuranceFormData;
};

const ModifierAvatar = ({
	pictureUrl,
	fallbackLabel,
}: {
	pictureUrl?: string;
	fallbackLabel: string;
}) => {
	const [imgError, setImgError] = useState(false);

	const initials =
		fallbackLabel.replace(/[^a-zA-ZæøåÆØÅ0-9]/g, "").slice(0, 2).toUpperCase() ||
		"?";

	return (
		<Avatar className="h-9 w-9">
			{pictureUrl && !imgError ? (
				<AvatarImage
					src={pictureUrl}
					alt=""
					onError={() => setImgError(true)}
				/>
			) : null}
			<AvatarFallback className="text-xs">{initials}</AvatarFallback>
		</Avatar>
	);
};

export const InsuranceModificationHistory = ({ insurance }: Props) => {
	const { t, i18n } = useTranslation();

	const hasHistory = insuranceHasModificationHistory(insurance);
	const parsed = hasHistory
		? parseInsuranceModifiedBy(insurance.modified_by)
		: null;
	const whenRaw = insurance.modified_at?.trim() ?? "";

	if (!hasHistory || !parsed || !whenRaw) {
		return null;
	}

	const displayName =
		parsed.nickname?.trim() || parsed.name?.trim() || "—";
	const whenLabel = formatInsuranceModifiedAt(whenRaw, i18n.language);

	const body = (
		<div className="space-y-3 text-left">
			<div>
				<p className="text-xs font-medium text-muted-foreground">
					{t("admin.insurances.modificationHistory.when")}
				</p>
				<p className="text-sm tabular-nums">{whenLabel}</p>
			</div>
			<div className="flex items-center gap-2 pt-1 border-t border-border">
				<ModifierAvatar
					key={parsed.picture ?? insurance.id}
					pictureUrl={parsed.picture}
					fallbackLabel={displayName}
				/>
				<div className="flex min-w-0 flex-col">
					<p className="text-xs font-medium text-muted-foreground">
						{t("admin.insurances.modificationHistory.who")}
					</p>
					<p className="text-sm font-medium truncate">{displayName}</p>
				</div>
			</div>
		</div>
	);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
					aria-label={t("admin.insurances.modificationHistory.ariaLabel")}
					title={t("admin.insurances.modificationHistory.tooltipHint")}
				>
					<History className="h-4 w-4" />
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-80 p-3" sideOffset={6}>
				{body}
			</PopoverContent>
		</Popover>
	);
};
